import React, { Fragment, useMemo, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/solid'
import InfinitySectionWrapper from './index'
import useGetCoursePlans from '../../../api/hooks/ambassador-batch/useGetCoursePlans'
import { BatchPlanProps, BatchPlanTypeModalProps } from '../../../utils/types'
import { transformPlans } from '../../../utils/utilities'
import { Button, Loader, Typography } from '@pw-tech/omni-ui'

const BatchPlanTypeModal: React.FC<BatchPlanTypeModalProps> = ({
  isOpen,
  onClose,
  getSelectedPlan,
  title = 'Batch Features',
}) => {
  const [planClicked, setPlanClicked] = useState<boolean>(false)

  const GetBatchDetails = () => {
    const batchDetails = JSON.parse(
      localStorage.getItem('BATCH_DETAILS') || '{}'
    )
    return batchDetails?._id
  }

  const { data, isLoading, error } = useGetCoursePlans({
    courseId: GetBatchDetails(),
    enabled: isOpen && !!GetBatchDetails(),
  })

  const isQueryEnabled = isOpen && !!GetBatchDetails()

  const batchPlans: BatchPlanProps[] = useMemo(
    () => transformPlans(data || []),
    [data]
  )

  const [selectedPlan, setSelectedPlan] = useState<BatchPlanProps | undefined>()

  useEffect(() => {
    if (batchPlans?.length > 0 && !selectedPlan) {
      const defaultPlan =
        batchPlans.find((plan) => plan?.isRecommended) || batchPlans[0]
      setSelectedPlan(defaultPlan)
    }
  }, [batchPlans, selectedPlan])

  const handlePlanChange = (data: any) => {
    const plan = batchPlans?.find((item) => item?.planId === data?.planId)
    if (plan) {
      setPlanClicked(true)
      setSelectedPlan(plan)
      getSelectedPlan(plan)
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setPlanClicked(false)
    }
  }, [isOpen])

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full w-[90%] sm:max-w-[60%] mx-auto sm:mr-[180px] items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-5xl rounded-2xl bg-transparent p-0 shadow-xl">
                <button
                  type="button"
                  onClick={onClose}
                  className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 text-black"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>

                {!isQueryEnabled && (
                  <div className="flex h-60 items-center justify-center bg-white text-black">
                    <Typography
                      variant="regular"
                      weight="semi-bold"
                      color="#3D3D3D"
                    >
                      Missing courseId. Please ensure BATCH_DETAILS is present
                      in localStorage.
                    </Typography>
                  </div>
                )}

                {isQueryEnabled && isLoading && (
                  <div className="flex h-60 items-center justify-center bg-white text-black">
                    <Typography
                      variant="regular"
                      weight="semi-bold"
                      color="#3D3D3D"
                    >
                      Loading plans...
                    </Typography>
                  </div>
                )}

                {!isLoading && error && (
                  <div className="flex h-60 items-center justify-center text-red-400 bg-white">
                    <Typography
                      variant="regular"
                      weight="semi-bold"
                      color="#3D3D3D"
                    >
                      Failed to load plans.
                    </Typography>
                  </div>
                )}

                {!isLoading && !error && batchPlans?.length === 0 && (
                  <div className="flex h-60 items-center justify-center text-black bg-white rounded-md">
                    <Typography
                      variant="regular"
                      weight="semi-bold"
                      color="#3D3D3D"
                    >
                      No plans available.
                    </Typography>
                  </div>
                )}

                {!isLoading &&
                  !error &&
                  batchPlans?.length > 0 &&
                  selectedPlan && (
                    <div>
                      <InfinitySectionWrapper
                        title={title}
                        batchPlans={batchPlans}
                        selectedPlan={selectedPlan}
                        planClicked={planClicked}
                        handlePlanSelect={handlePlanChange}
                        videoTitle=" Learn more about Infinity Batch"
                        videoDescription="Discover the benefits of Infinity Batch with this short video"
                      />

                      {batchPlans?.length === 1 && (
                        <Button
                          variant="lowFocus"
                          size="large"
                          className="w-full !h-[40px]"
                          disabled={planClicked}
                          preIcon={planClicked && <Loader size="extra-small" />}
                          onClick={() => {
                            setPlanClicked(true)
                            getSelectedPlan(selectedPlan)
                          }}
                        >
                          Select
                        </Button>
                      )}
                    </div>
                  )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default BatchPlanTypeModal
